import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { ArrowLeftIcon, MapPinIcon, CameraIcon, AnchorIcon, TagIcon, UsersIcon, ImageIcon, XIcon, ThermometerIcon, RulerIcon, ClockIcon, GlobeIcon, SendIcon } from 'lucide-react';
export const CreatePost: React.FC = () => {
  const navigate = useNavigate();
  const [postType, setPostType] = useState<'regular' | 'dive-log' | 'event'>('regular');
  const [content, setContent] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [diveDetails, setDiveDetails] = useState({
    depth: '',
    duration: '',
    temperature: ''
  });
  const [eventDetails, setEventDetails] = useState({
    title: '',
    date: '',
    time: '',
    type: ''
  });
  // Mock locations
  const popularLocations = [{
    name: 'Great Barrier Reef',
    distance: '5 km',
    image: 'https://images.unsplash.com/photo-1682687982167-d7fb3ed8541d?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80'
  }, {
    name: 'Osprey Reef',
    distance: '25 km',
    image: 'https://images.unsplash.com/photo-1570741066052-817c6de995c8?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80'
  }, {
    name: 'SS Yongala',
    distance: '350 km',
    image: 'https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80'
  }];
  // Mock image selection (in a real app, this would be file upload)
  const mockImages = ['https://images.unsplash.com/photo-1682687982167-d7fb3ed8541d?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80', 'https://images.unsplash.com/photo-1544551763-92ab472cad5d?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80', 'https://images.unsplash.com/photo-1570741066052-817c6de995c8?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80', 'https://images.unsplash.com/photo-1544552866-d3ed42536d26?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80'];
  const handleImageSelect = (image: string) => {
    if (selectedImages.includes(image)) {
      setSelectedImages(selectedImages.filter(img => img !== image));
    } else {
      setSelectedImages([...selectedImages, image]);
    }
  };
  const handleSubmit = () => {
    // In a real app, this would submit the post to an API
    navigate('/community');
  };
  const handleCancel = () => {
    navigate('/community');
  };
  return <div className="pb-20" data-id="element-755">
      {/* Header */}
      <div className="flex justify-between items-center mb-4" data-id="element-756">
        <div className="flex items-center" data-id="element-757">
          <Button variant="ghost" size="sm" className="mr-2" onClick={handleCancel} icon={<ArrowLeftIcon size={18} data-id="element-759" />} data-id="element-758" />
          <h1 className="text-xl font-bold text-navy" data-id="element-760">Create Post</h1>
        </div>
        <Button variant="primary" disabled={!content || !selectedLocation} onClick={handleSubmit} icon={<SendIcon size={16} data-id="element-762" />} data-id="element-761">
          Post
        </Button>
      </div>
      {/* Post Type Selector */}
      <Card className="mb-5" data-id="element-763">
        <div className="flex justify-between border-b border-gray-100 pb-4 mb-4" data-id="element-764">
          <Button variant={postType === 'regular' ? 'primary' : 'outline'} onClick={() => setPostType('regular')} className="flex-1 mr-2" data-id="element-765">
            Regular Post
          </Button>
          <Button variant={postType === 'dive-log' ? 'primary' : 'outline'} onClick={() => setPostType('dive-log')} className="flex-1 mr-2" icon={<AnchorIcon size={16} data-id="element-767" />} data-id="element-766">
            Dive Log
          </Button>
          <Button variant={postType === 'event' ? 'primary' : 'outline'} onClick={() => setPostType('event')} className="flex-1" icon={<UsersIcon size={16} data-id="element-769" />} data-id="element-768">
            Event
          </Button>
        </div>
        {/* User Info */}
        <div className="flex items-center mb-4" data-id="element-770">
          <img src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80" alt="Your profile" className="w-10 h-10 rounded-full mr-3" data-id="element-771" />
          <div data-id="element-772">
            <h3 className="font-medium" data-id="element-773">Your Name</h3>
            <div className="flex items-center text-sm text-gray-500" data-id="element-774">
              <GlobeIcon size={14} className="mr-1" data-id="element-775" />
              <span data-id="element-776">Public post</span>
            </div>
          </div>
        </div>
        {/* Post Content */}
        <div className="mb-4" data-id="element-777">
          <textarea placeholder={postType === 'regular' ? 'Share your dive experience...' : postType === 'dive-log' ? 'Describe your dive...' : 'Tell people about your event...'} className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary min-h-[120px] resize-none" value={content} onChange={e => setContent(e.target.value)} data-id="element-778" />
        </div>
        {/* Location Selector */}
        <div className="mb-4" data-id="element-779">
          {selectedLocation ? <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-780">
              <div className="flex items-center" data-id="element-781">
                <MapPinIcon size={18} className="text-primary mr-2" data-id="element-782" />
                <span data-id="element-783">{selectedLocation}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => {
            setSelectedLocation(null);
            setShowLocationSelector(true);
          }} data-id="element-784">
                Change
              </Button>
            </div> : <Button variant="outline" fullWidth className="justify-start" icon={<MapPinIcon size={18} data-id="element-786" />} onClick={() => setShowLocationSelector(true)} data-id="element-785">
              Add Location
            </Button>}
          {showLocationSelector && <div className="mt-3 border border-gray-200 rounded-lg p-3" data-id="element-787">
              <div className="flex justify-between items-center mb-3" data-id="element-788">
                <h3 className="font-medium" data-id="element-789">Select Location</h3>
                <Button variant="ghost" size="sm" icon={<XIcon size={16} data-id="element-791" />} onClick={() => setShowLocationSelector(false)} data-id="element-790" />
              </div>
              <input type="text" placeholder="Search locations..." className="w-full p-2 border border-gray-200 rounded-lg mb-3" data-id="element-792" />
              <h4 className="text-sm text-gray-500 mb-2" data-id="element-793">Popular Locations</h4>
              <div className="space-y-2" data-id="element-794">
                {popularLocations.map(location => <div key={location.name} className="flex items-center p-2 hover:bg-gray-50 rounded-lg cursor-pointer" onClick={() => {
              setSelectedLocation(location.name);
              setShowLocationSelector(false);
            }} data-id="element-795">
                    <div className="w-10 h-10 rounded-lg overflow-hidden mr-3" data-id="element-796">
                      <img src={location.image} alt={location.name} className="w-full h-full object-cover" data-id="element-797" />
                    </div>
                    <div data-id="element-798">
                      <h3 className="font-medium" data-id="element-799">{location.name}</h3>
                      <p className="text-xs text-gray-500" data-id="element-800">
                        {location.distance} away
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>}
        </div>
        {/* Dive Details (shown only for dive log posts) */}
        {postType === 'dive-log' && <div className="mb-4 p-3 bg-primary/5 rounded-lg border border-primary/20" data-id="element-801">
            <h3 className="font-medium mb-3 flex items-center" data-id="element-802">
              <AnchorIcon size={16} className="mr-2 text-primary" data-id="element-803" />
              Dive Details
            </h3>
            <div className="grid grid-cols-3 gap-3" data-id="element-804">
              <div data-id="element-805">
                <label className="block text-sm text-gray-600 mb-1 flex items-center" data-id="element-806">
                  <RulerIcon size={14} className="mr-1" data-id="element-807" />
                  Depth
                </label>
                <input type="text" placeholder="18m" className="w-full p-2 border border-gray-200 rounded-lg" value={diveDetails.depth} onChange={e => setDiveDetails({
              ...diveDetails,
              depth: e.target.value
            })} data-id="element-808" />
              </div>
              <div data-id="element-809">
                <label className="block text-sm text-gray-600 mb-1 flex items-center" data-id="element-810">
                  <ClockIcon size={14} className="mr-1" data-id="element-811" />
                  Duration
                </label>
                <input type="text" placeholder="45 min" className="w-full p-2 border border-gray-200 rounded-lg" value={diveDetails.duration} onChange={e => setDiveDetails({
              ...diveDetails,
              duration: e.target.value
            })} data-id="element-812" />
              </div>
              <div data-id="element-813">
                <label className="block text-sm text-gray-600 mb-1 flex items-center" data-id="element-814">
                  <ThermometerIcon size={14} className="mr-1" data-id="element-815" />
                  Temperature
                </label>
                <input type="text" placeholder="24°C" className="w-full p-2 border border-gray-200 rounded-lg" value={diveDetails.temperature} onChange={e => setDiveDetails({
              ...diveDetails,
              temperature: e.target.value
            })} data-id="element-816" />
              </div>
            </div>
          </div>}
        {/* Event Details (shown only for event posts) */}
        {postType === 'event' && <div className="mb-4 p-3 bg-primary/5 rounded-lg border border-primary/20" data-id="element-817">
            <h3 className="font-medium mb-3 flex items-center" data-id="element-818">
              <UsersIcon size={16} className="mr-2 text-primary" data-id="element-819" />
              Event Details
            </h3>
            <div className="space-y-3" data-id="element-820">
              <div data-id="element-821">
                <label className="block text-sm text-gray-600 mb-1" data-id="element-822">
                  Event Title
                </label>
                <input type="text" placeholder="Night Diving Workshop" className="w-full p-2 border border-gray-200 rounded-lg" value={eventDetails.title} onChange={e => setEventDetails({
              ...eventDetails,
              title: e.target.value
            })} data-id="element-823" />
              </div>
              <div className="grid grid-cols-2 gap-3" data-id="element-824">
                <div data-id="element-825">
                  <label className="block text-sm text-gray-600 mb-1" data-id="element-826">
                    Date
                  </label>
                  <input type="date" className="w-full p-2 border border-gray-200 rounded-lg" value={eventDetails.date} onChange={e => setEventDetails({
                ...eventDetails,
                date: e.target.value
              })} data-id="element-827" />
                </div>
                <div data-id="element-828">
                  <label className="block text-sm text-gray-600 mb-1" data-id="element-829">
                    Time
                  </label>
                  <input type="time" className="w-full p-2 border border-gray-200 rounded-lg" value={eventDetails.time} onChange={e => setEventDetails({
                ...eventDetails,
                time: e.target.value
              })} data-id="element-830" />
                </div>
              </div>
              <div data-id="element-831">
                <label className="block text-sm text-gray-600 mb-1" data-id="element-832">
                  Event Type
                </label>
                <select className="w-full p-2 border border-gray-200 rounded-lg" value={eventDetails.type} onChange={e => setEventDetails({
              ...eventDetails,
              type: e.target.value
            })} data-id="element-833">
                  <option value="" data-id="element-834">Select type</option>
                  <option value="Workshop" data-id="element-835">Workshop</option>
                  <option value="Cleanup" data-id="element-836">Cleanup</option>
                  <option value="Group Dive" data-id="element-837">Group Dive</option>
                  <option value="Training" data-id="element-838">Training</option>
                  <option value="Social" data-id="element-839">Social</option>
                </select>
              </div>
            </div>
          </div>}
        {/* Image Selection */}
        <div className="mb-4" data-id="element-840">
          <h3 className="font-medium mb-3 flex items-center" data-id="element-841">
            <ImageIcon size={16} className="mr-2 text-primary" data-id="element-842" />
            Add Photos
          </h3>
          {selectedImages.length > 0 && <div className="grid grid-cols-3 gap-2 mb-3" data-id="element-843">
              {selectedImages.map((image, index) => <div key={index} className="relative rounded-lg overflow-hidden h-24" data-id="element-844">
                  <img src={image} alt={`Selected ${index + 1}`} className="w-full h-full object-cover" data-id="element-845" />
                  <button className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1" onClick={() => handleImageSelect(image)} data-id="element-846">
                    <XIcon size={14} data-id="element-847" />
                  </button>
                </div>)}
            </div>}
          <div className="grid grid-cols-4 gap-2" data-id="element-848">
            {mockImages.map((image, index) => <div key={index} className={`relative rounded-lg overflow-hidden h-20 cursor-pointer ${selectedImages.includes(image) ? 'ring-2 ring-primary' : ''}`} onClick={() => handleImageSelect(image)} data-id="element-849">
                <img src={image} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" data-id="element-850" />
                {selectedImages.includes(image) && <div className="absolute inset-0 bg-primary/20 flex items-center justify-center" data-id="element-851">
                    <div className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center" data-id="element-852">
                      ✓
                    </div>
                  </div>}
              </div>)}
          </div>
        </div>
        {/* Tag People */}
        <Button variant="outline" fullWidth className="mb-4" icon={<TagIcon size={18} data-id="element-854" />} data-id="element-853">
          Tag People
        </Button>
        {/* Post Button */}
        <Button variant="primary" fullWidth disabled={!content || !selectedLocation} onClick={handleSubmit} data-id="element-855">
          {postType === 'regular' ? 'Share Post' : postType === 'dive-log' ? 'Log Dive' : 'Create Event'}
        </Button>
      </Card>
    </div>;
};